import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MAX_TOKENS_JE_VERANSTALTUNG,
  TOKEN_ANZEIGESTATUS_BESCHRIFTUNG,
  erlaubtTokenerzeugung,
} from '@mcikm-vote/shared';
import type { TokenAnzeigestatusWert, TokenUebersicht } from '@mcikm-vote/shared';

import { useErsatzFreigeben } from '../../admin/abstimmungenApi';
import {
  kartenPfad,
  useTeilnehmerAbmelden,
  useTeilnehmerSitzungBeenden,
  useTokenErsetzen,
  useTokenSperren,
  useTokens,
  useTokensErzeugen,
  useVeranstaltung,
} from '../../admin/veranstaltungenApi';
import { kopiereText } from '../../browser';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { EmailVersandAbschnitt } from '../../components/EmailVersandAbschnitt';
import { StatusZeichen } from '../../components/StatusZeichen';
import type { Tonart } from '../../components/StatusZeichen';
import { formatiereZeitpunkt } from '../../formatierung';

/** Farbliche **und** textliche Unterscheidung — §21 verbietet rein farbbasierte Information. */
function tonartFuer(status: TokenAnzeigestatusWert): Tonart {
  switch (status) {
    case 'angemeldet':
      return 'erfolg';
    case 'sitzung_getrennt':
      return 'warnung';
    case 'gesperrt':
      return 'fehler';
    default:
      return 'neutral';
  }
}

/**
 * Ob der Teilnehmer derzeit als anwesend gilt (§9.4).
 *
 * Anwesenheit hängt am Token, nicht an der Verbindung: Ein getrenntes Gerät bleibt anwesend, bis
 * abgemeldet, gesperrt oder die Veranstaltung beendet wird.
 */
function istAnwesend(token: TokenUebersicht): boolean {
  return token.anzeigestatus === 'angemeldet' || token.anzeigestatus === 'sitzung_getrennt';
}

const SEITENGROESSE = 50;

/** Die Aktionen, die eine Zeile der Tokenliste anbietet (§7.7, §7.8, §9.8). */
type Aktion = 'sperren' | 'ersetzen' | 'sitzung-beenden' | 'abmelden';

/**
 * Beschriftung und Bestätigungstext je Aktion.
 *
 * An **einer** Stelle statt in vier Verzweigungen: Der Unterschied zwischen „nur Sitzung beenden"
 * und „vollständig abmelden" ist der Kern von §9.8 und §9.9 — er muss im Dialog stehen, sonst
 * wählt niemand bewusst zwischen beiden.
 */
const AKTIONEN: Record<
  Aktion,
  { titel: string; bestaetigung: string; gefahr: boolean; text: (token: string) => string }
> = {
  sperren: {
    titel: 'Token sperren',
    bestaetigung: 'Sperren',
    gefahr: true,
    text: (token) =>
      `Der Token ${token} wird dauerhaft ungültig. Eine bestehende Sitzung endet sofort, eine ` +
      'erneute Anmeldung ist ausgeschlossen, und der Code wird nie erneut vergeben. Bereits ' +
      'abgegebene Stimmen bleiben erhalten und sind keinem Teilnehmer zuzuordnen.',
  },
  ersetzen: {
    titel: 'Token ersetzen',
    bestaetigung: 'Ersetzen',
    gefahr: false,
    text: (token) =>
      `Für ${token} entsteht ein technisch vollständig neuer Token. Es werden keine früheren ` +
      'Daten übertragen. Der bisherige Token wird ungültig; der Ersatz zählt zur Obergrenze.',
  },
  'sitzung-beenden': {
    titel: 'Nur die Sitzung beenden',
    bestaetigung: 'Sitzung beenden',
    gefahr: false,
    text: (token) =>
      `Das Gerät zu ${token} wird abgemeldet, der Teilnehmer bleibt jedoch anwesend. Die ` +
      'berücksichtigte Teilnehmerzahl einer laufenden Abstimmung ändert sich nicht, und eine ' +
      'erneute Anmeldung mit demselben Zugangscode ist jederzeit möglich.',
  },
  abmelden: {
    titel: 'Teilnehmer vollständig abmelden',
    bestaetigung: 'Abmelden',
    gefahr: true,
    text: (token) =>
      `${token} gilt danach als nicht mehr anwesend. Hat der Teilnehmer in einer laufenden ` +
      'Abstimmung noch nicht abgestimmt, sinkt die berücksichtigte Teilnehmerzahl; hat er bereits ' +
      'abgestimmt, bleibt seine Stimme erhalten und er bleibt bis zum Schließen in der ' +
      'Teilnehmerzahl dieser Abstimmung. Der Zugangscode bleibt gültig — eine erneute Anmeldung ' +
      'ist möglich.',
  },
};

/**
 * Tokenverwaltung einer Veranstaltung (§7).
 *
 * **Zeigt vollständige Tokens** (Änderungsanforderung ÄA-1). Zuvor stand hier `AB7K-••••••` — der
 * Veranstaltungscode ist nach §7.2 aber für alle Tokens derselben Veranstaltung gleich, sodass
 * jede Zeile identisch aussah und sich kein Token zum Sperren oder Ersetzen zuordnen ließ.
 *
 * Daraus folgt die Warnung am Seitenkopf: Diese Ansicht zeigt Zugangsdaten und gehört nicht auf
 * einen Beamer. Im Änderungsprotokoll erscheinen Tokens unverändert nur maskiert (§29.2).
 */
export function TokensSeite(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();

  const veranstaltung = useVeranstaltung(id);
  const tokens = useTokens(id);
  const erzeugen = useTokensErzeugen(id);
  const sperren = useTokenSperren();
  const ersetzen = useTokenErsetzen();
  const freigeben = useErsatzFreigeben();
  const sitzungBeenden = useTeilnehmerSitzungBeenden();
  const abmelden = useTeilnehmerAbmelden();

  const [anzahl, setAnzahl] = useState(25);
  const [seite, setSeite] = useState(0);
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<{
    token: TokenUebersicht;
    aktion: Aktion;
  } | null>(null);

  if (veranstaltung.isPending || tokens.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  if (veranstaltung.isError) {
    return (
      <p role="alert" className="text-fehler text-sm font-medium">
        {veranstaltung.error.message}
      </p>
    );
  }

  const v = veranstaltung.data;
  const alle = tokens.data ?? [];
  const gefiltert = filtereTokens(alle, suche);
  const seiten = Math.max(1, Math.ceil(gefiltert.length / SEITENGROESSE));
  // Nach einer Filterung kann die zuvor gewählte Seite hinter dem Ende liegen.
  const aktuelleSeite = Math.min(seite, seiten - 1);
  const sichtbar = gefiltert.slice(
    aktuelleSeite * SEITENGROESSE,
    (aktuelleSeite + 1) * SEITENGROESSE,
  );
  const verbleibend = MAX_TOKENS_JE_VERANSTALTUNG - v.tokensGesamt;
  const fehler =
    erzeugen.error ??
    sperren.error ??
    ersetzen.error ??
    freigeben.error ??
    sitzungBeenden.error ??
    abmelden.error;
  const ersatz = ersetzen.data;

  function fuehreAus(): void {
    if (gewaehlt === null) {
      return;
    }

    const beiErfolg = { onSuccess: () => setGewaehlt(null) };
    const ausfuehren = {
      sperren: () => sperren.mutate(gewaehlt.token.id, beiErfolg),
      ersetzen: () => ersetzen.mutate(gewaehlt.token.id, beiErfolg),
      'sitzung-beenden': () => sitzungBeenden.mutate(gewaehlt.token.id, beiErfolg),
      abmelden: () => abmelden.mutate(gewaehlt.token.id, beiErfolg),
    };
    ausfuehren[gewaehlt.aktion]();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Tokens</h1>
          <p className="text-text-gedaempft text-sm">
            {v.bezeichnung} · Code {v.code}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="ausgabe"
            className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche flex items-center rounded-md px-4 text-sm font-medium"
          >
            Token-Ausgabe
          </Link>
          {/* §8.3: Zugangskarten zum Ausdrucken. Die Datei enthält vollständige Tokens; jeder
              Abruf wird protokolliert. */}
          <a
            href={kartenPfad(id)}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
          >
            A6-Karten (PDF)
          </a>
          <Link
            to=".."
            relative="path"
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
          >
            Zur Veranstaltung
          </Link>
        </div>
      </div>

      {/*
        ÄA-1: Die Liste zeigt vollständige Zugangsdaten. Der Hinweis steht bewusst oben und nicht
        im Kleingedruckten — wer die Seite im Sitzungssaal öffnet, soll es vorher wissen.
      */}
      <p className="border-warnung/50 bg-warnung/10 rounded-md border p-3 text-sm">
        <strong>Diese Liste zeigt vollständige Zugangscodes.</strong> Sie ist für die Verwaltung
        gedacht — zum Sperren und Ersetzen muss erkennbar sein, um welchen Token es geht. Nicht auf
        einen Beamer geben und keine Bildschirmfotos weitergeben. Im Änderungsprotokoll erscheinen
        Tokens weiterhin ausschließlich maskiert.
      </p>

      {fehler !== null && fehler !== undefined && (
        <p role="alert" className="text-fehler text-sm font-medium">
          {fehler.message}
        </p>
      )}

      {/* §7.8: das Backend nennt vor einer Freigabe den Stand des ersetzten Tokens. */}
      {ersatz !== undefined && ersatz.alterTokenHatAbgestimmt !== null && (
        <p
          role="status"
          className="border-warnung/40 bg-flaeche-gedaempft rounded-md border p-3 text-sm"
        >
          {ersatz.alterTokenHatAbgestimmt
            ? `Der ersetzte Token ${ersatz.alt.token} hatte in der laufenden Abstimmung bereits ` +
              'abgestimmt. Der Ersatz gilt deshalb erst ab der nächsten Abstimmung; seine Stimme ' +
              'bleibt gezählt und ist nicht zuordenbar.'
            : `Der ersetzte Token ${ersatz.alt.token} hatte noch nicht abgestimmt und wurde aus ` +
              'der Statistik der laufenden Abstimmung entfernt. Der Ersatz kann für die laufende ' +
              'Abstimmung freigegeben werden.'}
        </p>
      )}

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Tokens erzeugen</h2>

        {erlaubtTokenerzeugung(v.status) ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="anzahl" className="text-sm font-medium">
                Anzahl
              </label>
              <input
                id="anzahl"
                type="number"
                min={1}
                max={verbleibend}
                value={anzahl}
                onChange={(ereignis) => {
                  setAnzahl(Number(ereignis.target.value));
                }}
                className="border-rand bg-hintergrund min-h-touchflaeche w-32 rounded-md border px-3"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                erzeugen.mutate(anzahl);
              }}
              disabled={erzeugen.isPending || anzahl < 1 || anzahl > verbleibend}
              className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche rounded-md px-4 font-medium disabled:pointer-events-none disabled:opacity-60"
            >
              {erzeugen.isPending ? 'Wird erzeugt …' : 'Erzeugen'}
            </button>
            <p className="text-text-gedaempft text-xs">
              {v.tokensGesamt} von höchstens {MAX_TOKENS_JE_VERANSTALTUNG} erzeugt, {verbleibend}{' '}
              verbleiben. Gesperrte und ersetzte Tokens zählen mit.
            </p>
          </div>
        ) : (
          <p className="text-text-gedaempft text-sm">
            Für eine beendete oder archivierte Veranstaltung können keine Tokens mehr erzeugt
            werden.
          </p>
        )}
      </section>

      <EmailVersandAbschnitt veranstaltungId={id} />

      {gewaehlt !== null && (
        <BestaetigungsDialog
          titel={AKTIONEN[gewaehlt.aktion].titel}
          beschreibung={AKTIONEN[gewaehlt.aktion].text(gewaehlt.token.token)}
          bestaetigungstext={AKTIONEN[gewaehlt.aktion].bestaetigung}
          gefahr={AKTIONEN[gewaehlt.aktion].gefahr}
          laeuft={
            sperren.isPending ||
            ersetzen.isPending ||
            sitzungBeenden.isPending ||
            abmelden.isPending
          }
          onBestaetigen={fuehreAus}
          onAbbrechen={() => {
            setGewaehlt(null);
          }}
        />
      )}

      {alle.length === 0 ? (
        <p className="border-rand bg-flaeche text-text-gedaempft rounded-xl border p-4 text-sm">
          Es wurden noch keine Tokens erzeugt.
        </p>
      ) : (
        <>
          {/*
            Suche über der Liste. Bei bis zu 1.000 Tokens je Veranstaltung (§7.5) ist Blättern kein
            Weg, um einen bestimmten zu finden — etwa wenn ein Teilnehmer mit seiner Karte vorne
            steht. Gefiltert wird im Browser: Die Liste liegt ohnehin vollständig vor, und eine
            Serverabfrage je Tastendruck brächte nichts als Last.
          */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <label htmlFor="token-suche" className="text-sm font-medium">
                Token suchen
              </label>
              <input
                id="token-suche"
                type="search"
                value={suche}
                placeholder="Code eingeben, etwa P3X9R2 oder AB7K-P3X9"
                autoComplete="off"
                onChange={(ereignis) => {
                  setSuche(ereignis.target.value);
                  setSeite(0);
                }}
                className="border-rand bg-hintergrund min-h-touchflaeche rounded-md border px-3 py-2"
              />
            </div>
            {suche.trim() !== '' && (
              <p className="text-text-gedaempft self-end pb-2 text-sm">
                {gefiltert.length} von {alle.length}
                {gefiltert.length === 0 && ' — keine Übereinstimmung'}
              </p>
            )}
          </div>

          <div className="border-rand bg-flaeche overflow-x-auto rounded-xl border shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-rand text-text-gedaempft border-b text-left">
                <tr>
                  <th className="p-3 font-medium">Token</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Erste Anmeldung</th>
                  <th className="p-3 font-medium">Letzte Verbindung</th>
                  <th className="p-3 font-medium">Abstimmung</th>
                  <th className="p-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((token) => (
                  <tr key={token.id} className="border-rand border-b last:border-b-0">
                    <td className="p-3">
                      <TokenZelle token={token.token} versendetAm={token.perEmailVersendetAm} />
                    </td>
                    <td className="p-3">
                      <StatusZeichen
                        beschriftung={TOKEN_ANZEIGESTATUS_BESCHRIFTUNG[token.anzeigestatus]}
                        tonart={tonartFuer(token.anzeigestatus)}
                      />
                    </td>
                    <td className="p-3">
                      {token.angemeldetAm === null ? (
                        <span className="text-text-gedaempft">—</span>
                      ) : (
                        formatiereZeitpunkt(token.angemeldetAm)
                      )}
                    </td>
                    <td className="p-3">
                      {token.letzteVerbindungAm === null ? (
                        <span className="text-text-gedaempft">—</span>
                      ) : (
                        formatiereZeitpunkt(token.letzteVerbindungAm)
                      )}
                    </td>
                    <td className="p-3">
                      {/* §9.8: ausschließlich „abgestimmt" oder „nicht abgestimmt" — nie die Auswahl. */}
                      {token.ersatzGesperrt ? (
                        <StatusZeichen
                          beschriftung="Erst ab nächster Abstimmung"
                          tonart="warnung"
                        />
                      ) : token.hatAbgestimmt === null ? (
                        <span className="text-text-gedaempft">—</span>
                      ) : token.hatAbgestimmt ? (
                        <StatusZeichen beschriftung="Stimme abgegeben" tonart="erfolg" />
                      ) : (
                        <StatusZeichen beschriftung="Noch nicht abgestimmt" />
                      )}
                    </td>
                    <td className="p-3">
                      {token.status === 'unbenutzt' ? (
                        <div className="flex flex-wrap gap-2">
                          {/*
                          §9.8 nennt drei Möglichkeiten nebeneinander: nur die Sitzung beenden,
                          vollständig abmelden, den Token sperren. Sie erscheinen nur, wenn sie
                          etwas bewirken — „Sitzung beenden" braucht eine Sitzung, „Abmelden" einen
                          anwesenden Teilnehmer.
                        */}
                          {token.verbunden && (
                            <button
                              type="button"
                              onClick={() => {
                                setGewaehlt({ token, aktion: 'sitzung-beenden' });
                              }}
                              className="hover:text-akzent underline"
                            >
                              Sitzung beenden
                            </button>
                          )}
                          {istAnwesend(token) && (
                            <button
                              type="button"
                              onClick={() => {
                                setGewaehlt({ token, aktion: 'abmelden' });
                              }}
                              className="hover:text-akzent underline"
                            >
                              Abmelden
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setGewaehlt({ token, aktion: 'sperren' });
                            }}
                            className="hover:text-akzent underline"
                          >
                            Sperren
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setGewaehlt({ token, aktion: 'ersetzen' });
                            }}
                            className="hover:text-akzent underline"
                          >
                            Ersetzen
                          </button>
                          {token.ersatzGesperrt && (
                            <button
                              type="button"
                              disabled={freigeben.isPending}
                              onClick={() => {
                                freigeben.mutate(token.id);
                              }}
                              className="hover:text-akzent underline disabled:pointer-events-none disabled:opacity-60"
                            >
                              Für laufende Abstimmung freigeben
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-text-gedaempft">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {seiten > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button
            type="button"
            onClick={() => {
              setSeite((s) => Math.max(0, s - 1));
            }}
            disabled={aktuelleSeite === 0}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-3 disabled:pointer-events-none disabled:opacity-60"
          >
            Zurück
          </button>
          <span className="text-text-gedaempft">
            Seite {aktuelleSeite + 1} von {seiten}
          </span>
          <button
            type="button"
            onClick={() => {
              setSeite((s) => Math.min(seiten - 1, s + 1));
            }}
            disabled={aktuelleSeite >= seiten - 1}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-3 disabled:pointer-events-none disabled:opacity-60"
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Filtert die Tokenliste nach einer Sucheingabe (ÄA-1).
 *
 * Verglichen wird gegen die **Normalform** ohne Bindestrich und in Großschreibung — genau wie bei
 * der Anmeldung nach §7.1, wo Leerzeichen, Bindestriche und Kleinschreibung ebenfalls unerheblich
 * sind. Wer `ab7k-p3x` eintippt oder `P3X9R2` von einer Karte abliest, findet dieselbe Zeile.
 *
 * Gesucht wird als Teilzeichenfolge und nicht nur am Anfang: Auf der Zugangskarte steht der
 * Berechtigungscode hinten, und genau nach ihm sucht man im Regelfall.
 */
export function filtereTokens(liste: readonly TokenUebersicht[], suche: string): TokenUebersicht[] {
  const gesucht = suche.replace(/[\s-]/g, '').toUpperCase();
  if (gesucht === '') {
    return [...liste];
  }

  return liste.filter((eintrag) =>
    eintrag.token.replace(/[\s-]/g, '').toUpperCase().includes(gesucht),
  );
}

/**
 * Ein Token in der Tabelle, mit Schaltfläche zum Kopieren.
 *
 * Der Sinn der Klartextanzeige ist, den Token greifen zu können — etwa um ihn einem Teilnehmer
 * vorzulesen oder in ein Formular zu übernehmen. Das Kopieren läuft über {@link kopiereText},
 * weil `navigator.clipboard` ohne sicheren Kontext fehlt (etwa bei Zugriff über
 * `http://<IP>:3250`); ohne den Umweg wirkte die Schaltfläche dort tot.
 *
 * `versendetAm` zeigt bei hybriden Veranstaltungen an, dass dieser Token bereits per E-Mail
 * vergeben ist (ÄA-7) — er erscheint deshalb auch nicht mehr in Ausgabeansicht, CSV-Export und
 * PDF-Karten. Zu welcher Adresse er ging, steht bewusst nirgends.
 */
function TokenZelle({
  token,
  versendetAm,
}: {
  token: string;
  versendetAm: string | null;
}): ReactElement {
  const [kopiert, setKopiert] = useState(false);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-mono">{token}</span>
        <button
          type="button"
          title={`${token} kopieren`}
          aria-label={`Token ${token} kopieren`}
          onClick={() => {
            void kopiereText(token).then((gelungen) => {
              setKopiert(gelungen);
              window.setTimeout(() => {
                setKopiert(false);
              }, 2000);
            });
          }}
          className="text-text-gedaempft hover:text-akzent text-xs underline"
        >
          {kopiert ? 'kopiert' : 'kopieren'}
        </button>
      </div>
      {versendetAm !== null && (
        <span title={`Versendet am ${formatiereZeitpunkt(versendetAm)}`} className="w-fit">
          <StatusZeichen beschriftung="Per E-Mail versendet" tonart="neutral" />
        </span>
      )}
    </div>
  );
}
